/** Sticky action bar for Edit on a CUSTOM row (E3, spec 2026-09-18 D15, §6
 * Edit). ProcessConfigureBar's twin over the draft lane: save with what
 * the session is based on (the stamp + the live version, SA-404) → debounced
 * `/draft/validate` of the document on screen → publish the STORED draft.
 * Kind-agnostic: an order row's raw editor rides the same bar. Semantic
 * truth stays server-side. */
import { useEffect, useRef, useState } from 'react'
import {
  draftConflict,
  useDiscardWorkflowDraft,
  usePublishWorkflowDraft,
  useSaveWorkflowDraft,
  useValidateWorkflowDraft,
  useWorkflowDraft,
  type DraftConflict,
} from '@/hooks/useWorkflowDraft'
import { useProcessDraftStore, type AuthorableDefinitionJson } from '@/stores/processDraftStore'
import { Button, Modal, ModalBody, ModalContent, ModalFooter, ModalHeader, ModalTitle } from '@/components/ui'
import type { WorkflowDraftFinding, WorkflowDraftValidateResponse } from '@/types/workflowDefinition'
import { extractPublishFindings } from '../configure/publishFindings'
import { describeDraftDiff, isProcessDiff } from './draftDiffText'
import { DRAFT_CONFLICT_TEXT } from './draftConflictText'
import { findingCard, type CardId } from './editorCards'


export function WorkflowDraftBar({
  workflowId,
  onReviewCard,
}: {
  workflowId: string
  /** Opens the card a finding names (process rows only). */
  onReviewCard?: (cardId: CardId) => void
}) {
  const store = useProcessDraftStore()
  const draft = useWorkflowDraft(workflowId)
  const save = useSaveWorkflowDraft(workflowId)
  const validate = useValidateWorkflowDraft(workflowId)
  const publish = usePublishWorkflowDraft(workflowId)
  const discard = useDiscardWorkflowDraft(workflowId)
  const [validation, setValidation] = useState<WorkflowDraftValidateResponse | null>(null)
  const [validateFailed, setValidateFailed] = useState(false)
  const [conflict, setConflict] = useState<DraftConflict | null>(null)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [confirm, setConfirm] = useState<'publish' | 'discard' | null>(null)
  const [publishFindings, setPublishFindings] = useState<WorkflowDraftFinding[] | null>(null)
  const dirty = store.isDirty()
  const hasStoredDraft = store.expectedUpdatedAt !== null
  const docJson = JSON.stringify(store.doc)
  const docJsonRef = useRef(docJson)
  docJsonRef.current = docJson

  // Debounced server validation of the document ON SCREEN (the endpoint is
  // stateless), with the stale-response guard ProcessConfigureBar uses.
  // `baseVersionNumber` is also a dep: a Reload onto a moved base (a restore
  // or repin over a KEPT draft) can carry byte-identical text, and the diff
  // on screen must be against the version that is actually live.
  useEffect(() => {
    setValidation(null) // a result for other content must never gate Publish
    setValidateFailed(false)
    setPublishFindings(null)
    const issuedFor = docJson
    const timer = setTimeout(() => {
      validate.mutate(JSON.parse(issuedFor), {
        onSuccess: (result) => {
          if (docJsonRef.current === issuedFor) {
            setValidation(result)
            // SA-1060 (ruling Q3): the cards' marks read this diff; a `{}` (findings) or an
            // order diff writes nothing, so the marks keep the last process diff.
            if (isProcessDiff(result.diff)) store.setDiff(result.diff)
          }
        },
        onError: () => {
          if (docJsonRef.current === issuedFor) setValidateFailed(true)
        },
      })
    }, 600)
    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docJson, store.baseVersionNumber, workflowId])

  /** Every write carries what this session is based on; the server refuses
   * one whose basis moved (409) and nothing is written. */
  const basis = { expectedUpdatedAt: store.expectedUpdatedAt, baseVersionNumber: store.baseVersionNumber }

  function handleSave() {
    const sent = store.doc
    if (!sent) return
    // The session this save belongs to, captured now (SA-1410): an answer
    // after Exit or a move to another workflow must not touch the next one.
    const startedIn = workflowId
    setSaveError(null)
    save.mutate(
      { draft: sent as unknown as Record<string, unknown>, ...basis },
      {
        // The clean point is what was SENT — the author may have kept typing.
        onSuccess: (data) => store.markSaved(sent, data.draft_updated_at, startedIn),
        onError: (error) => {
          const refused = draftConflict(error)
          if (refused) setConflict(refused)
          else setSaveError("Couldn't save the draft — try again.")
        },
      },
    )
  }

  async function handleReload() {
    setSaveError(null)
    const result = await draft.refetch()
    // A failed refetch still carries the CACHED draft: beginning from it would
    // wipe the unsaved edits and clear the banner for nothing.
    if (!result.isSuccess || result.isError) return setSaveError("Couldn't reload the draft — try again.")
    const data = result.data
    const doc = (data.draft ?? data.current_definition) as AuthorableDefinitionJson | null
    if (!doc) return
    store.begin(workflowId, doc, data.draft ? data.draft_updated_at : null, data.current_version_number)
    setConflict(null)
  }

  function handleDiscard() {
    setConfirm(null)
    setSaveError(null)
    if (!hasStoredDraft) return store.stop()
    discard.mutate(
      { expectedUpdatedAt: store.expectedUpdatedAt },
      {
        onSuccess: () => store.stop(),
        onError: (error) => {
          // A refused discard keeps the session: the newer draft is not this
          // author's to remove, and their unsaved edits are not the server's.
          const refused = draftConflict(error)
          if (refused) setConflict(refused)
          else setSaveError("Couldn't discard the draft — try again.")
        },
      },
    )
  }

  function handlePublish() {
    publish.mutate(basis, {
      onSuccess: () => store.stop(),
      onError: (error) => {
        const refused = draftConflict(error)
        if (refused) {
          setConfirm(null)
          setConflict(refused)
          publish.reset() // or the next dialog opens on "Couldn't publish"
          return
        }
        // The stored draft failed the server's own validation: show its
        // findings on the bar, keep the draft.
        const found = extractPublishFindings(error)
        if (found) {
          setPublishFindings(found)
          setConfirm(null)
          publish.reset() // or the next Publish dialog opens on "Couldn't publish"
        }
      },
    })
  }

  const findings = publishFindings ?? (validation && !validation.valid ? validation.findings : null)
  const canPublish = !dirty && hasStoredDraft && validation?.valid === true && !save.isPending && conflict === null
  const publishTitle =
    dirty || !hasStoredDraft
      ? 'Save the draft first'
      : conflict !== null
        ? 'Reload first'
        : validation === null
          ? 'Checking the draft…'
          : !validation.valid
            ? 'Fix what the check found first'
            : undefined
  const diffLines = validation ? describeDraftDiff(validation.diff) : []

  // opaque base + primary tint overlay — sticky bar must not let content bleed through
  return (
    <div className="sticky bottom-0 z-10 space-y-2 rounded-xl border bg-card bg-linear-to-t from-primary/10 to-primary/10 border-primary/20 px-4 py-2">
      {conflict && (
        <div role="alert" className="flex items-center justify-between gap-3 text-xs text-warning-strong">
          <span>{DRAFT_CONFLICT_TEXT[conflict]} Reloading replaces your unsaved changes.</span>
          <Button size="sm" variant="outline" isLoading={draft.isFetching} onClick={handleReload}>
            Reload
          </Button>
        </div>
      )}
      {saveError && <p role="alert" className="text-xs text-destructive">{saveError}</p>}
      {validateFailed && (
        <p className="text-xs text-warning-strong">
          Couldn't check this draft with the server — it will retry on your next change.
        </p>
      )}
      {findings && findings.length > 0 && (
        <ul aria-label="Findings" className="max-h-32 space-y-0.5 overflow-y-auto text-xs text-destructive">
          {findings.map((f, i) => {
            const card = findingCard(f.path)
            return (
              <li key={i}>
                {card && onReviewCard ? (
                  <button
                    type="button"
                    className="font-mono underline underline-offset-2"
                    onClick={() => onReviewCard(card)}
                  >
                    {f.path}
                  </button>
                ) : (
                  <span className="font-mono">{f.path || 'document'}</span>
                )}
                : {f.message}
              </li>
            )
          })}
        </ul>
      )}
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs text-foreground">
          Editing this workflow's draft.{' '}
          {dirty ? (
            <span className="font-medium">Unsaved changes.</span>
          ) : hasStoredDraft ? (
            <span>Draft saved.</span>
          ) : (
            <span>No changes yet.</span>
          )}
        </p>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="ghost" onClick={() => store.stop()}>
            Exit
          </Button>
          <Button
            size="sm"
            variant="ghost"
            disabled={(!dirty && !hasStoredDraft) || discard.isPending}
            onClick={() => setConfirm('discard')}
          >
            Discard draft
          </Button>
          <Button
            size="sm"
            variant="secondary"
            disabled={!dirty || save.isPending || conflict !== null}
            isLoading={save.isPending}
            onClick={handleSave}
          >
            {save.isPending ? 'Saving…' : 'Save draft'}
          </Button>
          <Button size="sm" disabled={!canPublish} title={publishTitle} onClick={() => setConfirm('publish')}>
            Publish
          </Button>
        </div>
      </div>

      {confirm === 'discard' && (
        <Modal open onOpenChange={(o) => { if (!o) setConfirm(null) }}>
          <ModalContent size="sm" showClose={false}>
            <ModalHeader>
              <ModalTitle>Discard this draft?</ModalTitle>
            </ModalHeader>
            <ModalBody>
              <p className="text-sm">
                The draft and any unsaved changes are removed. The published version is not affected.
              </p>
            </ModalBody>
            <ModalFooter>
              <Button size="sm" variant="ghost" onClick={() => setConfirm(null)}>
                Cancel
              </Button>
              <Button size="sm" variant="destructive" onClick={handleDiscard}>
                Discard
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}
      {confirm === 'publish' && validation && (
        <Modal open onOpenChange={(o) => { if (!o) setConfirm(null) }}>
          <ModalContent size="sm" showClose={false}>
            <ModalHeader>
              <ModalTitle>Publish this draft?</ModalTitle>
            </ModalHeader>
            <ModalBody className="space-y-3">
              {diffLines.length > 0 ? (
                <ul aria-label="What changes" className="list-disc space-y-0.5 pl-5 text-sm">
                  {diffLines.map((line) => (
                    <li key={line}>{line}</li>
                  ))}
                </ul>
              ) : 'changed' in validation.diff ? (
                // An order row's diff is one boolean, and it said "unchanged".
                <p className="text-sm">This draft is the same as the live version.</p>
              ) : (
                <p className="text-sm">No structural changes — wording and settings only.</p>
              )}
              <p className="text-xs text-muted-foreground">
                This becomes the live version for new records. Records already in progress keep the steps
                they started with.
              </p>
              {publish.isError && !publishFindings && (
                <p role="alert" className="text-xs text-destructive">Couldn't publish — try again.</p>
              )}
            </ModalBody>
            <ModalFooter>
              <Button size="sm" variant="ghost" onClick={() => setConfirm(null)}>
                Cancel
              </Button>
              <Button
                size="sm"
                isLoading={publish.isPending}
                disabled={publish.isPending}
                onClick={handlePublish}
              >
                Publish
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}
    </div>
  )
}
